import { getAccessCode } from './accessCode';
import { pickMockResponse } from './mockData';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

/**
 * Analyze an image URL for credibility.
 * @param {string} imageUrl
 * @param {AbortSignal} [signal]
 * @returns {Promise<object>} response matching the backend's schema
 */
export async function analyzeImage(imageUrl, signal) {
  if (USE_MOCK) return mockAnalyze(imageUrl, signal);

  const code = getAccessCode();
  if (!code) throw new ApiError('Missing access code', 401, null);

  let response;
  try {
    response = await fetch(`${API_BASE}/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Access-Code': code,
      },
      body: JSON.stringify({ image_url: imageUrl }),
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError('Network error — could not reach the analysis service.', 0, null);
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    // server returned non-JSON; fall through with null payload
  }

  if (payload?.stage3?.parse_error && typeof payload.stage3.raw === 'string') {
    const match = payload.stage3.raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    const jsonStr = match ? match[1] : payload.stage3.raw;
    try {
      payload = { ...payload, stage3: JSON.parse(jsonStr) };
    } catch {
      // raw couldn't be recovered; leave stage3 as-is so the UI skips it
      payload = { ...payload, stage3: null };
    }
  }

  if (!response.ok) {
    const message =
      payload?.detail ||
      payload?.message ||
      (response.status === 401
        ? 'Invalid access code.'
        : response.status === 429
          ? 'Rate limit reached — please wait a moment and try again.'
          : `Analysis failed (HTTP ${response.status}).`);
    throw new ApiError(message, response.status, payload);
  }

  return payload;
}

/**
 * Verify an access code by hitting the backend.
 * In mock mode, any non-empty code is accepted.
 */
export async function verifyAccessCode(code) {
  if (USE_MOCK) {
    await delay(450);
    if (!code || code.length < 4) {
      throw new ApiError('Access code must be at least 4 characters.', 401, null);
    }
    return { ok: true };
  }

  let response;
  try {
    response = await fetch(`${API_BASE}/health`, {
      method: 'GET',
      headers: { 'X-Access-Code': code },
    });
  } catch {
    throw new ApiError('Could not reach the service. Check your connection.', 0, null);
  }

  if (response.status === 401) throw new ApiError('Invalid access code.', 401, null);
  if (!response.ok) throw new ApiError(`Verification failed (HTTP ${response.status}).`, response.status, null);
  return { ok: true };
}

// ─── Mock helpers ──────────────────────────────────────────────────────────

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(t);
        reject(new DOMException('Aborted', 'AbortError'));
      });
    }
  });
}

async function mockAnalyze(imageUrl, signal) {
  if (!imageUrl || !imageUrl.trim()) {
    throw new ApiError('Please provide an image URL.', 400, null);
  }
  // Simulate 1.6–2.4s analysis time
  const ms = 1600 + Math.floor(Math.random() * 800);
  await delay(ms, signal);
  return pickMockResponse(imageUrl);
}
